import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { isEqual } from 'lodash';
import { selectWidgetEffectiveCols } from '../../redux/datasourceSlice.js';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import Select from '../../../common/Select.jsx';
import SettingsSection from '../../settings_sidebar/common/SettingsSection.jsx';
import SelectSetting from '../../settings_sidebar/common/SelectSetting.jsx';
import SliderSetting from '../../settings_sidebar/common/SliderSetting.jsx';
import ColorPickerSetting from '../../settings_sidebar/ag_chart/components/ColorPickerSetting.jsx';
import ColumnSelectSetting from '../../settings_sidebar/ag_chart/components/ColumnSelectSetting.jsx';
import { saveWidgetQuery } from '../../datasource_v2/api.js';
import { datasourceKey, parseDatasourceKey } from '../../datasource_v2/constants.js';
import { selectUsesDatasourceV2 } from '../../datasource_v2/selectors.js';

const EMPTY_SETTINGS = {};

function ButtonFilterWidgetSettings({ widgetId }) {
  const setAttribute = useSetWidgetAttribute(widgetId);
  const cols = useSelector(selectWidgetEffectiveCols(widgetId));
  const settings = useSelector(state => state.dashboard.widgets[widgetId]?.settings ?? EMPTY_SETTINGS);
  const currentDatasource = useSelector(state => state.dashboard.widgets[widgetId]?.data_datasource);
  const dashboardId = useSelector(state => state.dashboard.dashboardId);
  const usesDatasourceV2 = useSelector(selectUsesDatasourceV2(widgetId));
  const datasourceV2Id = useSelector(state => state.dashboard.widgets[widgetId]?.datasource_v2_id);

  // Collect datasources used in this dashboard from the centralized store.
  // isEqual prevents re-renders when the derived array has the same content.
  const dashboardDatasources = useSelector(
    state => Object.values(state.datasources.datasources || {}).map(ds => ({
      id: ds.id,
      name: ds.name,
      output_type: ds.output_type,
      config: ds.config,
    })),
    isEqual
  );

  const datasourceOpts = useMemo(
    () => dashboardDatasources.map(ds => ({ value: ds.id, label: ds.name })),
    [dashboardDatasources]
  );

  // v2 datasources ('v2-<id>') are stored on the widget through the v2 endpoint; the server
  // then lists the distinct values of the picked column
  const setDatasourceV2 = async (nextId) => {
    const saved = await saveWidgetQuery(dashboardId, widgetId, { datasourceId: nextId, query: nextId ? { output: 'rows' } : null });
    setAttribute('datasource_v2_id', saved.datasource_v2_id);
    setAttribute('datasource_query', saved.datasource_query);
  };

  const selectDatasource = async (id) => {
    const v2Id = parseDatasourceKey(id);
    if (v2Id != null) {
      await setDatasourceV2(v2Id);
      return;
    }
    if (usesDatasourceV2) await setDatasourceV2(null);

    const ds = dashboardDatasources.find(d => d.id === id);
    if (!ds) return;
    setAttribute('data_datasource', {
      id: ds.id,
      name: ds.name,
      output_type: ds.output_type,
      config: ds.config,
      filter_model: currentDatasource?.filter_model || {},
      group_model: currentDatasource?.group_model || {},
    });
  };

  const columnDefs = useMemo(() =>
    cols.map(c => ({ value: c.accessor, label: c.name || c.accessor })),
    [cols]
  );

  const selectedObject = settings;
  const settingProps = {
    selectedObject,
    setAttribute: (path, val) => setAttribute(`settings.${path}`, val),
    columnDefs,
  };

  return (
    <>
      <CollapsibleSection title={I18n.t('data')}>
        <label>{I18n.t('source')}</label>
        <Select
          options={datasourceOpts}
          placeholder={I18n.t('click_to_define')}
          value={usesDatasourceV2 ? datasourceKey(datasourceV2Id) : currentDatasource?.id}
          onChange={selectDatasource}
          name='button-filter-datasource-input'
        />
      </CollapsibleSection>
      <hr />
      <CollapsibleSection title={I18n.t('content_settings')}>
        <SettingsSection>
          <ColumnSelectSetting 
            {...settingProps}
            attr='columnId'
            label={I18n.t('filter_column')}
            hideDescription
          />
          <SelectSetting
            {...settingProps}
            attr='wrap'
            label={I18n.t('layout')}
            isClearable={false}
            options={[
              { value: true, label: I18n.t('wrap') },
              { value: false, label: I18n.t('no_wrap') },
            ]}
          />
          <SelectSetting
            {...settingProps}
            attr='showAllButton'
            label={I18n.t('show_all_button')}
            isClearable={false}
            options={[
              { value: false, label: I18n.t('hidden') },
              { value: true, label: I18n.t('shown') },
            ]}
          />
          <label className=''>{I18n.t('label')}</label>
          <input
            type='text'
            className='form-control form-control-sm mb-1'
            value={settings.label || ''}
            onChange={e => setAttribute('settings.label', e.target.value)}
            placeholder={I18n.t('optional')}
          />
        </SettingsSection>
      </CollapsibleSection>
      <hr />
      <CollapsibleSection title={I18n.t('style')} initialCollapsed={true}>
        <SettingsSection>
          <SliderSetting 
            {...settingProps} 
            attr='labelFontSize' 
            min={8} 
            max={48} 
            label={I18n.t('label_font_size')} 
            hideDescription
          />
          <ColorPickerSetting 
            {...settingProps}
            attr='labelColor'
            label={I18n.t('label_text_color')}
            hideDescription
          />
          <SliderSetting
            {...settingProps}
            attr='buttonFontSize'
            min={8}
            max={32}
            hideDescription
            label={I18n.t('button_font_size')} 
          />
          <SliderSetting
            {...settingProps}
            attr='paddingV'
            min={0}
            max={40}
            hideDescription
            label={I18n.t('padding_vertical')} 
          />
          <SliderSetting
            {...settingProps}
            attr='paddingH'
            min={0}
            max={40}
            hideDescription
            label={I18n.t('padding_horizontal')} 
          />
          <SliderSetting
            {...settingProps}
            attr='gap'
            min={0}
            max={32}
            hideDescription
            label={I18n.t('gap')} 
          />
          <SliderSetting
            {...settingProps}
            attr='borderRadius'
            min={0}
            max={24}
            hideDescription
            label={I18n.t('border_radius')} 
          />
          
          <SelectSetting
            {...settingProps}
            attr='verticalAlign'
            label={I18n.t('vertical_alignment')}
            isClearable={false}
            hideDescription
            options={[
              { value: 'top',    label: I18n.t('top') },
              { value: 'center', label: I18n.t('center') },
              { value: 'bottom', label: I18n.t('bottom') },
            ]}
          />
          <SelectSetting
            {...settingProps}
            attr='horizontalAlign'
            label={I18n.t('horizontal_alignment')}
            hideDescription
            isClearable={false}
            options={[
              { value: 'left',   label: I18n.t('left') },
              { value: 'center', label: I18n.t('center') },
              { value: 'right',  label: I18n.t('right') },
            ]}
          />
        </SettingsSection>
        <hr />
        <SettingsSection>
          <ColorPickerSetting {...settingProps} attr='bgColor' label={I18n.t('background_color')} />
          <ColorPickerSetting {...settingProps} attr='fgColor' label={I18n.t('text_color')} />
          <ColorPickerSetting {...settingProps} attr='activeBgColor' label={I18n.t('active_background_color')} />
          <ColorPickerSetting {...settingProps} attr='activeFgColor' label={I18n.t('active_text_color')} />
        </SettingsSection>
      </CollapsibleSection>
    </>
  );
}

export default ButtonFilterWidgetSettings;
